/**
 * Materials catalog hook (customer-facing).
 */

import { useQuery, type UseQueryOptions } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import type { Catalog } from "@/api/types";

import { queryKeys } from "./keys";

const API = "/api/v1";

// --- Materials catalog ------------------------------------------------

export function useCatalog(
  lang: string,
  options?: Omit<UseQueryOptions<Catalog>, "queryKey" | "queryFn">,
) {
  return useQuery<Catalog>({
    queryKey: queryKeys.catalog(lang),
    queryFn: () => apiFetch<Catalog>(`${API}/materials`, { lang }),
    // Catalog rarely changes; keep it in cache for 10 minutes so
    // hopping between pages doesn't re-fetch.
    staleTime: 10 * 60 * 1000,
    ...options,
  });
}

/**
 * Variant for the public catalog browse page (`/materials`).
 *
 * Same shape as `useCatalog`, but passes `include_unpriced=true` so
 * admins-added-but-not-yet-priced materials still surface — they
 * just offer no axis values. The
 * catalog page is informational ("here's what we cut"), not
 * transactional ("pick a variant"), so dead-end risk doesn't
 * apply. Distinct query key keeps it from poisoning the
 * configurator's cache.
 */
export function useCatalogIncludingUnpriced(
  lang: string,
  options?: Omit<UseQueryOptions<Catalog>, "queryKey" | "queryFn">,
) {
  return useQuery<Catalog>({
    queryKey: [...queryKeys.catalog(lang), "include_unpriced"],
    queryFn: () =>
      apiFetch<Catalog>(`${API}/materials?include_unpriced=true`, { lang }),
    staleTime: 10 * 60 * 1000,
    ...options,
  });
}
